import { useQuery } from '@tanstack/react-query'
import { diasEntre, fimPorDuracao } from '@orbind/core'
import { useMemo, useState } from 'react'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useLabels, useWiMutation, type WorkItemDetail } from './api.ts'
import { NumberStepper } from './bits.tsx'
import { ListaDePessoas } from './ListaDePessoas.tsx'

/**
 * Paineis popover da barra "Adicionar": Etiquetas, Membros e Datas.
 * Layout e comportamento copiados da fatia 02 do prototipo.
 */

/**
 * Mascara dd/mm/aaaa aplicada enquanto se digita: mantem so digitos e insere
 * as barras nas posicoes certas, sem atrapalhar o apagar.
 */
function mascaraDeData(valor: string) {
  const digitos = valor.replace(/\D/g, '').slice(0, 8)
  if (digitos.length <= 2) return digitos
  if (digitos.length <= 4) return `${digitos.slice(0, 2)}/${digitos.slice(2)}`
  return `${digitos.slice(0, 2)}/${digitos.slice(2, 4)}/${digitos.slice(4)}`
}

/** Paleta de cores de etiqueta do prototipo. */
export const LABEL_COLORS = ['#1B4DF5', '#19B27B', '#E09112', '#E5484D', '#7C3AED', '#DB2777', '#0D9488', '#47548C']

export function LabelsPanel({ detail, onClose }: { detail: WorkItemDetail; onClose: () => void }) {
  const workItemId = detail.item.id
  const labels = useLabels()
  const [query, setQuery] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')

  const toggle = useWiMutation(workItemId, async (labelId: string) =>
    api['work-items'][':id'].labels[':labelId'].toggle.$post({ param: { id: workItemId, labelId } }),
  )
  const create = useWiMutation(workItemId, async (name: string) => {
    const used = new Set((labels.data?.labels ?? []).map((l) => l.color))
    const color = LABEL_COLORS.find((c) => !used.has(c)) ?? LABEL_COLORS[0]!
    const response = await (await api['work-items'].labels.$post({ json: { name, color } })).json()
    // etiqueta nova ja entra vinculada ao item, como no prototipo
    await api['work-items'][':id'].labels[':labelId'].toggle.$post({
      param: { id: workItemId, labelId: response.label!.id },
    })
  })
  const rename = useWiMutation(workItemId, async (input: { labelId: string; name?: string; color?: string }) =>
    api['work-items'].labels[':labelId'].$patch({
      param: { labelId: input.labelId },
      json: { ...(input.name !== undefined && { name: input.name }), ...(input.color !== undefined && { color: input.color }) },
    }),
  )

  const attached = new Set(detail.collab.labels.map((l) => l.id))
  const rows = useMemo(
    () =>
      (labels.data?.labels ?? []).filter((label) => label.name.toLowerCase().includes(query.trim().toLowerCase())),
    [labels.data, query],
  )

  return (
    <>
      <div className="wi-panel__backdrop" onClick={onClose} />
      <div className="wi-panel">
        <div className="wi-panel__head">
          <div className="wi-panel__title">Etiquetas</div>
          <button type="button" className="wi-panel__close" aria-label="Fechar" title="Fechar" onClick={onClose}>
            <i className="fas fa-times" aria-hidden style={{ fontSize: 12 }} />
          </button>
        </div>
        <input
          className="wi-panel__search"
          placeholder="Buscar etiquetas…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="wi-panel__list">
          {rows.map((label) => {
            const isOn = attached.has(label.id)
            const isEditing = editingId === label.id
            return (
              <div key={label.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  className={`wi-checkbox${isOn ? ' wi-checkbox--on' : ''}`}
                  aria-label="Selecionar etiqueta"
                  aria-pressed={isOn}
                  onClick={() => toggle.mutate(label.id)}
                >
                  <i className="fas fa-check" aria-hidden />
                </button>
                {isEditing ? (
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input
                        className="wi-draft__input"
                        style={{ height: 32, width: '100%', paddingRight: 34, fontSize: 12 }}
                        autoFocus
                        placeholder="Nome da etiqueta"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            rename.mutate({ labelId: label.id, name: editName })
                            setEditingId(null)
                          }
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                      />
                      <button
                        type="button"
                        aria-label="Confirmar alteração"
                        title="Confirmar alteração"
                        style={{
                          position: 'absolute',
                          right: 4,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 24,
                          height: 24,
                          border: 'none',
                          borderRadius: 6,
                          background: 'var(--ob-primary)',
                          color: 'var(--ob-on-primary)',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          rename.mutate({ labelId: label.id, name: editName })
                          setEditingId(null)
                        }}
                      >
                        <i className="fas fa-check" aria-hidden style={{ fontSize: 10 }} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {LABEL_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={`wi-color${label.color === color ? ' wi-color--on' : ''}`}
                          style={{ background: color }}
                          aria-label="Alterar cor da etiqueta"
                          title="Alterar cor"
                          onClick={() => rename.mutate({ labelId: label.id, color })}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="wi-labelbar"
                      style={{ background: label.color }}
                      onClick={() => toggle.mutate(label.id)}
                    >
                      {label.name}
                    </button>
                    <button
                      type="button"
                      className="wi-cl__iconbtn"
                      aria-label="Renomear etiqueta"
                      title="Renomear etiqueta"
                      onClick={() => {
                        setEditingId(label.id)
                        setEditName(label.name)
                      }}
                    >
                      <i className="fas fa-pen" aria-hidden style={{ fontSize: 10 }} />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
        <button
          type="button"
          className="wi-panel__create"
          onClick={() => {
            const name = query.trim() || 'Nova etiqueta'
            create.mutate(name)
            setQuery('')
          }}
        >
          Criar uma nova etiqueta
        </button>
      </div>
    </>
  )
}

export function MembersPanel({
  detail,
  members,
  onClose,
}: {
  detail: WorkItemDetail
  members: Bootstrap['members']
  onClose: () => void
}) {
  const workItemId = detail.item.id
  const memberIds = new Set(detail.collab.memberIds)
  const toggle = useWiMutation(workItemId, async (memberId: string) =>
    api['work-items'][':id'].members[':memberId'].toggle.$post({ param: { id: workItemId, memberId } }),
  )
  /**
   * A ESTRELA DA LINHA (pedido 2026-09-21).
   *
   * Definir o responsável sem sair para o campo "Responsável": a mesma lista
   * que já diz quem acompanha passa a dizer quem responde. Estrela de novo em
   * quem já é responsável deixa o item sem responsável.
   *
   * O servidor tira essa pessoa dos membros — os dois papéis são distintos, e
   * ninguém ocupa os dois ao mesmo tempo.
   */
  const responsavelAtual = detail.item.assigneeMemberId
  const definirResponsavel = useWiMutation(workItemId, async (memberId: string | null) =>
    api['work-items'][':id'].$patch({
      param: { id: workItemId },
      query: {},
      json: { assigneeMemberId: memberId },
    }),
  )

  return (
    <>
      <div className="wi-panel__backdrop" onClick={onClose} />
      <div className="wi-panel" style={{ width: 290 }}>
        <div className="wi-panel__head">
          <div className="wi-panel__title">Membros</div>
          <button type="button" className="wi-panel__close" aria-label="Fechar" title="Fechar" onClick={onClose}>
            <i className="fas fa-times" aria-hidden style={{ fontSize: 12 }} />
          </button>
        </div>
        <div className="wi-panel__list" style={{ maxHeight: 320, gap: 2 }}>
          <ListaDePessoas
            pessoas={members.filter((person) => person.isActive)}
            ehMembro={(id) => memberIds.has(id)}
            responsavelId={responsavelAtual ?? null}
            onAlternarMembro={(id) => toggle.mutate(id)}
            onDefinirResponsavel={(id) => definirResponsavel.mutate(id)}
            onFechar={onClose}
            tamanhoDoAvatar={28}
          />
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------- datas ---

const DOWS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

const toInput = (date: Date | null) =>
  date ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date) : ''

const parseInput = (value: string): Date | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim())
  if (!match) return null
  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
  return Number.isNaN(date.getTime()) ? null : date
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

export function DatesPanel({ detail, onClose }: { detail: WorkItemDetail; onClose: () => void }) {
  const item = detail.item
  const [startOn, setStartOn] = useState(item.startAt !== null)
  const [dueOn, setDueOn] = useState(item.dueAt !== null)
  const [start, setStart] = useState(toInput(item.startAt ? new Date(item.startAt) : null))
  const [due, setDue] = useState(toInput(item.dueAt ? new Date(item.dueAt) : null))
  /** Campo que recebe o clique do calendario — segue o cursor. */
  const [campoFocado, setCampoFocado] = useState<'inicio' | 'entrega'>('entrega')
  const [time, setTime] = useState(
    item.dueAt ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(item.dueAt)) : '18:00',
  )
  /** Hora do inicio — mesmo par data+hora que a entrega ja tinha. */
  const [startTime, setStartTime] = useState(
    item.startAt ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(item.startAt)) : '09:00',
  )
  /**
   * Quantidade de dias e antecessor (2026-08-31).
   *
   * Com duracao e sem data, o cronograma PREVE as datas: o item comeca quando
   * o antecessor termina e dura os dias informados. Com as duas datas postas a
   * mao, elas mandam e a quantidade de dias vira o intervalo entre elas.
   */
  const [dias, setDias] = useState(item.durationDays != null ? String(item.durationDays) : '')
  const [antecessor, setAntecessor] = useState(item.predecessorId ?? '')
  const irmaos = useQuery({
    queryKey: ['work-items', 'irmaos', item.parentWorkItemId],
    enabled: Boolean(item.parentWorkItemId),
    queryFn: async () => {
      const r = await api['work-items'].$get({ query: { parentId: item.parentWorkItemId as string, limit: 200 as never } })
      const d = (await r.json()) as { items: { id: string; code: string; title: string }[] }
      return d.items.filter((x) => x.id !== item.id)
    },
  })
  const [recurrence, setRecurrence] = useState(item.recurrence)
  const [reminder, setReminder] = useState(item.reminder)
  const [cursor, setCursor] = useState(() => (item.dueAt ? new Date(item.dueAt) : new Date()))

  const save = useWiMutation(
    item.id,
    async (input: { startAt: string | null; dueAt: string | null; durationDays: number | null; predecessorId: string | null }) =>
      api['work-items'][':id'].$patch({
        param: { id: item.id },
        query: {},
        json: {
          startAt: input.startAt,
          dueAt: input.dueAt,
          durationDays: input.durationDays,
          predecessorId: input.predecessorId,
          recurrence,
          reminder,
        } as never,
      }),
  )

  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const gridStart = new Date(first)
    gridStart.setDate(first.getDate() - first.getDay())
    return Array.from({ length: 35 }, (_, i) => {
      const day = new Date(gridStart)
      day.setDate(gridStart.getDate() + i)
      return day
    })
  }, [cursor])

  const startDate = parseInput(start)
  const dueDate = parseInput(due)
  const today = new Date()

  const commit = () => {
    /** Junta a data com a hora digitada ao lado dela. */
    const comHora = (data: Date, hora: string, padraoH: number) => {
      const combinada = new Date(data)
      const [h, m] = hora.split(':').map(Number)
      combinada.setHours(Number.isNaN(h) ? padraoH : (h ?? padraoH), Number.isNaN(m) ? 0 : (m ?? 0), 0, 0)
      return combinada
    }

    const startAt = startOn && startDate ? comHora(startDate, startTime, 9) : null
    const dueAt = dueOn && dueDate ? comHora(dueDate, time, 18) : null
    const n = Number(dias)
    // com as duas datas a mao, a quantidade de dias e o intervalo entre elas
    const duracao =
      startAt && dueAt
        ? diasEntre(startAt.toISOString(), dueAt.toISOString())
        : dias.trim() === '' || Number.isNaN(n)
          ? null
          : Math.max(0, Math.round(n))

    save.mutate(
      {
        startAt: startAt ? startAt.toISOString() : null,
        dueAt: dueAt ? dueAt.toISOString() : null,
        durationDays: duracao,
        predecessorId: antecessor || null,
      },
      { onSuccess: onClose },
    )
  }

  const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(cursor)

  return (
    <>
      <div className="wi-panel__backdrop" onClick={onClose} />
      <div className="wi-panel desc-scroll" style={{ width: 300, maxHeight: 'min(74vh, 580px)', overflowY: 'auto', padding: 12, zIndex: 50 }}>
        <div className="wi-panel__head" style={{ marginBottom: 10 }}>
          <div className="wi-panel__title">Datas</div>
          <button type="button" className="wi-panel__close" aria-label="Fechar" title="Fechar" onClick={onClose}>
            <i className="fas fa-times" aria-hidden style={{ fontSize: 12 }} />
          </button>
        </div>

        <div className="wi-cal__nav">
          <button type="button" className="wi-cal__navbtn" aria-label="Ano anterior" onClick={() => setCursor(new Date(cursor.getFullYear() - 1, cursor.getMonth(), 1))}>
            <i className="fas fa-angle-double-left" aria-hidden style={{ fontSize: 10 }} />
          </button>
          <button type="button" className="wi-cal__navbtn" aria-label="Mês anterior" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
            <i className="fas fa-angle-left" aria-hidden style={{ fontSize: 10 }} />
          </button>
          <span className="wi-cal__label">{monthLabel}</span>
          <button type="button" className="wi-cal__navbtn" aria-label="Próximo mês" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
            <i className="fas fa-angle-right" aria-hidden style={{ fontSize: 10 }} />
          </button>
          <button type="button" className="wi-cal__navbtn" aria-label="Próximo ano" onClick={() => setCursor(new Date(cursor.getFullYear() + 1, cursor.getMonth(), 1))}>
            <i className="fas fa-angle-double-right" aria-hidden style={{ fontSize: 10 }} />
          </button>
        </div>

        <div className="wi-cal__grid">
          {DOWS.map((dow) => (
            <span key={dow} className="wi-cal__dow">
              {dow}
            </span>
          ))}
          {days.map((day) => {
            const isStart = startDate && sameDay(day, startDate)
            const isDue = dueDate && sameDay(day, dueDate)
            const inRange = startDate && dueDate && day > startDate && day < dueDate
            return (
              <button
                key={day.toISOString()}
                type="button"
                className={[
                  'wi-cal__day',
                  day.getMonth() !== cursor.getMonth() && 'wi-cal__day--outside',
                  sameDay(day, today) && 'wi-cal__day--today',
                  (isStart || isDue) && 'wi-cal__day--sel',
                  inRange && 'wi-cal__day--range',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  // O clique preenche o campo que esta com o cursor: trocar de
                  // campo e clicar de novo preenche o outro. Sem foco em
                  // nenhum, vale a entrega (o caso mais comum).
                  if (campoFocado === 'inicio') {
                    setStart(toInput(day))
                    setStartOn(true)
                  } else {
                    setDue(toInput(day))
                    setDueOn(true)
                  }
                }}
              >
                {day.getDate()}
              </button>
            )
          })}
        </div>

        <div className="wi-cal__fieldlabel">Data de início</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button
            type="button"
            className={`wi-checkbox${startOn ? ' wi-checkbox--on' : ''}`}
            aria-label="Usar data de início"
            aria-pressed={startOn}
            onClick={() => setStartOn((v) => !v)}
          >
            <i className="fas fa-check" aria-hidden />
          </button>
          <input
            className="wi-cal__input"
            placeholder="dd/mm/aaaa"
            disabled={!startOn}
            value={start}
            onFocus={() => setCampoFocado('inicio')}
            onChange={(e) => setStart(mascaraDeData(e.target.value))}
          />
          <input
            className="wi-cal__input"
            style={{ flex: 'none', width: 64 }}
            placeholder="09:00"
            aria-label="Hora de início"
            disabled={!startOn}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>

        <div className="wi-cal__fieldlabel">Data de entrega</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button
            type="button"
            className={`wi-checkbox${dueOn ? ' wi-checkbox--on' : ''}`}
            aria-label="Usar data de entrega"
            aria-pressed={dueOn}
            onClick={() => setDueOn((v) => !v)}
          >
            <i className="fas fa-check" aria-hidden />
          </button>
          <input className="wi-cal__input" placeholder="dd/mm/aaaa" disabled={!dueOn} value={due} onFocus={() => setCampoFocado('entrega')} onChange={(e) => setDue(mascaraDeData(e.target.value))} />
          <input
            className="wi-cal__input"
            style={{ flex: 'none', width: 64 }}
            placeholder="18:00"
            disabled={!dueOn}
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>

        {/* Calendário, datas e SÓ ENTÃO a duração (ordem de 2026-08-31): quem
            põe data resolve em cima; quem planeja pela cadeia desce até aqui. */}
        <div className="wi-cal__fieldlabel">Quantidade de dias</div>
        <div style={{ marginBottom: 6 }}>
          <NumberStepper
            label="Quantidade de dias"
            suffix="dia(s)"
            disabled={startOn && dueOn}
            value={
              startOn && dueOn && startDate && dueDate
                ? diasEntre(startDate.toISOString(), dueDate.toISOString())
                : dias === ''
                  ? null
                  : Number(dias)
            }
            onChange={(v) => {
              setDias(v == null ? '' : String(v))
              // com inicio marcado, mexer nos dias ja projeta a entrega
              if (startOn && startDate && v != null) {
                setDue(toInput(new Date(fimPorDuracao(startDate.toISOString(), v))))
              }
            }}
          />
        </div>
        <p className="wi-cal__note">
          {startOn && dueOn
            ? 'As datas acima mandam — os dias são o intervalo entre elas.'
            : 'Sem data, o prazo sai da cadeia: o item começa quando o antecessor termina e dura estes dias.'}
        </p>

        <div className="wi-cal__fieldlabel">Antecessor</div>
        <select
          className="wi-cal__select"
          value={antecessor}
          disabled={!item.parentWorkItemId}
          onChange={(e) => setAntecessor(e.target.value)}
        >
          <option value="">
            {item.parentWorkItemId ? 'Anterior na ordem (padrão)' : 'Sem antecessor — ancora no início do pai'}
          </option>
          {(irmaos.data ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.code} · {x.title}
            </option>
          ))}
        </select>
        <p className="wi-cal__note">
          O item começa no dia em que o antecessor termina. Sem antecessor, ele ancora no início do item pai.
        </p>

        <div className="wi-cal__fieldlabel">Recorrente</div>
        <select className="wi-cal__select" value={recurrence} onChange={(e) => setRecurrence(e.target.value as typeof recurrence)}>
          <option value="never">Nunca</option>
          <option value="daily">Diariamente</option>
          <option value="weekly">Semanalmente</option>
          <option value="monthly">Mensalmente</option>
          <option value="yearly">Anualmente</option>
        </select>

        <div className="wi-cal__fieldlabel">Definir lembrete</div>
        <select className="wi-cal__select" style={{ marginBottom: 6 }} value={reminder} onChange={(e) => setReminder(e.target.value as typeof reminder)}>
          <option value="1d">1 dia antes</option>
          <option value="none">Sem lembrete</option>
          <option value="2h">2 horas antes</option>
          <option value="1h">1 hora antes</option>
        </select>
        <p className="wi-cal__note">Lembretes serão enviados a todos os membros deste item pela central de notificações.</p>

        <button type="button" className="wi-cal__save" onClick={commit}>
          Salvar
        </button>
        <button
          type="button"
          className="wi-cal__remove"
          onClick={() =>
            save.mutate(
              { startAt: null, dueAt: null, durationDays: null, predecessorId: null },
              { onSuccess: onClose },
            )
          }
        >
          Remover
        </button>
      </div>
    </>
  )
}
